import Phaser from 'phaser'
import { Player } from '../entities/Player'
import {
  InventoryItem,
  ITEMS,
  INVENTORY_EVENTS
} from '../types/GameTypes'

const MAX_INVENTORY_SLOTS = 20

export class InventorySystem {
  private scene: Phaser.Scene
  private player: Player

  // Inventory slots (fixed 20 slots)
  private slots: (InventoryItem | null)[]

  // Gold stored separately
  private gold: number = 0

  constructor(scene: Phaser.Scene, player: Player) {
    this.scene = scene
    this.player = player

    // Initialize empty inventory
    this.slots = new Array(MAX_INVENTORY_SLOTS).fill(null)
  }

  /**
   * Add gold to the player's wallet
   */
  public addGold(amount: number): void {
    this.gold += amount
    this.scene.events.emit(INVENTORY_EVENTS.GOLD_CHANGED, this.gold)
    this.scene.events.emit(INVENTORY_EVENTS.INVENTORY_CHANGED)
  }

  /**
   * Remove gold from the player's wallet
   */
  public removeGold(amount: number): boolean {
    if (this.gold < amount) return false

    this.gold -= amount
    this.scene.events.emit(INVENTORY_EVENTS.GOLD_CHANGED, this.gold)
    this.scene.events.emit(INVENTORY_EVENTS.INVENTORY_CHANGED)
    return true
  }

  /**
   * Get current gold amount
   */
  public getGold(): number {
    return this.gold
  }

  /**
   * Add an item to the inventory
   * Returns the quantity actually added (may be less if inventory full)
   */
  public addItem(itemId: string, quantity: number = 1): number {
    const itemDef = ITEMS[itemId]
    if (!itemDef) return 0

    let remaining = quantity
    let totalAdded = 0

    // For stackable items, try to add to existing stacks first
    if (itemDef.stackable) {
      for (let i = 0; i < this.slots.length && remaining > 0; i++) {
        const slot = this.slots[i]
        if (slot && slot.itemId === itemId) {
          const canAdd = Math.min(remaining, itemDef.maxStack - slot.quantity)
          if (canAdd > 0) {
            slot.quantity += canAdd
            remaining -= canAdd
            totalAdded += canAdd
          }
        }
      }
    }

    // Add to empty slots
    for (let i = 0; i < this.slots.length && remaining > 0; i++) {
      if (!this.slots[i]) {
        const qtyToAdd = itemDef.stackable
          ? Math.min(remaining, itemDef.maxStack)
          : 1

        this.slots[i] = { itemId, quantity: qtyToAdd }
        remaining -= qtyToAdd
        totalAdded += qtyToAdd

        // Non-stackable items use one slot each
        if (!itemDef.stackable) {
          remaining--
        }
      }
    }

    if (totalAdded > 0) {
      this.scene.events.emit(INVENTORY_EVENTS.ITEM_ADDED, { itemId, quantity: totalAdded })
      this.scene.events.emit(INVENTORY_EVENTS.INVENTORY_CHANGED)
    }

    return totalAdded
  }

  /**
   * Remove an item from the inventory
   * Returns the quantity actually removed
   */
  public removeItem(itemId: string, quantity: number = 1): number {
    let remaining = quantity
    let totalRemoved = 0

    for (let i = this.slots.length - 1; i >= 0 && remaining > 0; i--) {
      const slot = this.slots[i]
      if (slot && slot.itemId === itemId) {
        const toRemove = Math.min(remaining, slot.quantity)
        slot.quantity -= toRemove
        remaining -= toRemove
        totalRemoved += toRemove

        // Remove empty slot
        if (slot.quantity <= 0) {
          this.slots[i] = null
        }
      }
    }

    if (totalRemoved > 0) {
      this.scene.events.emit(INVENTORY_EVENTS.ITEM_REMOVED, { itemId, quantity: totalRemoved })
      this.scene.events.emit(INVENTORY_EVENTS.INVENTORY_CHANGED)
    }

    return totalRemoved
  }

  /**
   * Use an item (potions)
   * Returns true if item was used successfully
   */
  public useItem(itemId: string): boolean {
    const itemDef = ITEMS[itemId]
    if (!itemDef) return false

    // Check if player has the item
    const count = this.getItemCount(itemId)
    if (count <= 0) return false

    // Apply item effects
    if (itemDef.type === 'potion' && itemDef.effect) {
      // Health potion
      if (itemDef.effect.hp && itemDef.effect.hp > 0) {
        // Don't use if already full HP
        if (this.player.stats.currentHp >= this.player.stats.maxHp) {
          return false
        }
        this.player.heal(itemDef.effect.hp)
      }

      // Mana potion
      if (itemDef.effect.mp && itemDef.effect.mp > 0) {
        // Don't use if already full MP
        if (this.player.stats.currentMp >= this.player.stats.maxMp) {
          return false
        }
        this.player.restoreMana(itemDef.effect.mp)
      }

      // Remove item from inventory
      this.removeItem(itemId, 1)

      this.scene.events.emit(INVENTORY_EVENTS.ITEM_USED, { itemId })
      return true
    }

    return false
  }

  /**
   * Get the count of a specific item in inventory
   */
  public getItemCount(itemId: string): number {
    let count = 0
    for (const slot of this.slots) {
      if (slot && slot.itemId === itemId) {
        count += slot.quantity
      }
    }
    return count
  }

  /**
   * Check if inventory has at least one of an item
   */
  public hasItem(itemId: string): boolean {
    return this.getItemCount(itemId) > 0
  }

  /**
   * Get all inventory slots
   */
  public getSlots(): (InventoryItem | null)[] {
    return [...this.slots]
  }

  /**
   * Get item at specific slot index
   */
  public getSlot(index: number): InventoryItem | null {
    if (index < 0 || index >= this.slots.length) return null
    return this.slots[index]
  }

  /**
   * Get number of free slots
   */
  public getFreeSlots(): number {
    return this.slots.filter(s => s === null).length
  }

  /**
   * Check if inventory is full
   */
  public isFull(): boolean {
    return this.getFreeSlots() === 0
  }

  /**
   * Get total count of health potions (all types)
   */
  public getHealthPotionCount(): number {
    return this.getItemCount('healthPotion') + this.getItemCount('smallHealthPotion')
  }

  /**
   * Get total count of mana potions
   */
  public getManaPotionCount(): number {
    return this.getItemCount('manaPotion')
  }

  /**
   * Use best available health potion
   * Prefers small potions when HP loss is small
   */
  public useHealthPotion(): boolean {
    const hpMissing = this.player.stats.maxHp - this.player.stats.currentHp

    if (hpMissing <= 0) return false

    // Use small potion if HP loss is less than 30 or no big potions
    if ((hpMissing <= 30 || !this.hasItem('healthPotion')) && this.hasItem('smallHealthPotion')) {
      return this.useItem('smallHealthPotion')
    }

    // Use regular health potion
    if (this.hasItem('healthPotion')) {
      return this.useItem('healthPotion')
    }

    // Fallback to small potion
    if (this.hasItem('smallHealthPotion')) {
      return this.useItem('smallHealthPotion')
    }

    return false
  }

  /**
   * Use mana potion
   */
  public useManaPotion(): boolean {
    return this.useItem('manaPotion')
  }

  /**
   * Clean up
   */
  public destroy(): void {
    // Nothing to clean up
  }
}
